/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { Layers, Plus, Trash2, Users, Search } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import Spinner from '@/components/ui/Spinner'
import StatusBadge from '@/components/ui/StatusBadge'
import { formatDate } from '@/lib/utils'
import type { ContactStatus } from '@/types/database'

const schema = z.object({ name: z.string().min(1, 'Name is required') })
type SegmentForm = z.infer<typeof schema>

export default function SegmentsPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [memberSearch, setMemberSearch] = useState('')
  const [addSearch, setAddSearch] = useState('')

  const { data: segments, isLoading } = useQuery({
    queryKey: ['segments', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('segments')
        .select('*, segment_contacts(count)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
      return data ?? []
    },
    enabled: !!user,
  })

  const { data: contacts } = useQuery({
    queryKey: ['contacts-for-segment', user?.id, selected],
    queryFn: async () => {
      if (!selected) return []
      const { data } = await supabase
        .from('segment_contacts')
        .select('contact:contacts(id, email, first_name, last_name, company, status, joined_at)')
        .eq('segment_id', selected)
      type ContactSub = { id: string; email: string; first_name: string | null; last_name: string | null; company: string | null; status: string; joined_at: string }
      return (data ?? []).map(r => r.contact as unknown as ContactSub).filter(Boolean)
    },
    enabled: !!selected,
  })

  const { data: allContacts } = useQuery({
    queryKey: ['contacts-subscribed', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('contacts')
        .select('id, email, first_name, last_name')
        .eq('user_id', user!.id)
        .eq('status', 'SUBSCRIBED')
        .order('email')
      return data ?? []
    },
    enabled: !!user,
  })

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<SegmentForm>({
    resolver: zodResolver(schema),
  })

  const createSegment = useMutation({
    mutationFn: async (data: SegmentForm) => {
      const { error } = await supabase.from('segments').insert({ ...data, user_id: user!.id })
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['segments'] }); reset(); setShowAdd(false) },
  })

  const deleteSegment = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('segments').delete().eq('id', id).eq('user_id', user!.id)
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['segments'] }); setSelected(null) },
  })

  const addToSegment = useMutation({
    mutationFn: async ({ segmentId, contactId }: { segmentId: string; contactId: string }) => {
      const { error } = await supabase.from('segment_contacts').upsert(
        { segment_id: segmentId, contact_id: contactId },
        { onConflict: 'segment_id,contact_id', ignoreDuplicates: true }
      )
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts-for-segment', 'segments'] }),
  })

  const removeFromSegment = useMutation({
    mutationFn: async ({ segmentId, contactId }: { segmentId: string; contactId: string }) => {
      const { error } = await supabase.from('segment_contacts')
        .delete().eq('segment_id', segmentId).eq('contact_id', contactId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts-for-segment', 'segments'] }),
  })

  const segmentContactIds = new Set(contacts?.map(c => c.id))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Segments</h1>
          <p className="text-sm text-gray-500 mt-1">Group contacts for targeted issues</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="btn-primary">
          <Plus className="w-4 h-4" /> New segment
        </button>
      </div>

      {showAdd && (
        <div className="card p-5">
          <form onSubmit={handleSubmit(d => createSegment.mutate(d))} className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="label">Segment name</label>
              <input className="input" placeholder="e.g. Fintech, London startups…" {...register('name')} />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
            </div>
            <button type="submit" disabled={isSubmitting} className="btn-primary">Create</button>
            <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Segment list */}
        <div className="col-span-1 space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !segments?.length ? (
            <EmptyState icon={Layers} title="No segments" description="Create a segment to target specific contacts." />
          ) : (
            segments.map(seg => (
              <button
                key={seg.id}
                onClick={() => setSelected(seg.id === selected ? null : seg.id)}
                className={`w-full text-left card p-4 transition-colors ${selected === seg.id ? 'border-brand-400 bg-brand-50' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{seg.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(seg.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {(seg.segment_contacts as Array<{ count: number }>)?.[0]?.count ?? 0}
                    </span>
                    <button
                      onClick={e => { e.stopPropagation(); confirm('Delete segment?') && deleteSegment.mutate(seg.id) }}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Segment membership */}
        <div className="col-span-2">
          {selected ? (
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                <h3 className="font-semibold text-sm text-gray-900">
                  {segments?.find(s => s.id === selected)?.name} — {contacts?.length ?? 0} member{(contacts?.length ?? 0) !== 1 ? 's' : ''}
                </h3>
                <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
                  <Search className="w-3 h-3 text-gray-400" />
                  <input type="text" placeholder="Search members…" value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    className="outline-none text-xs text-gray-700 placeholder-gray-400 w-40 bg-transparent" />
                </div>
              </div>
              {/* Add contacts */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs text-gray-500">Add subscribers to this segment:</p>
                  <input type="text" placeholder="Filter…" value={addSearch}
                    onChange={e => setAddSearch(e.target.value)}
                    className="text-xs px-2 py-1 rounded border border-gray-200 bg-white outline-none w-40" />
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                  {allContacts
                    ?.filter(c => !segmentContactIds.has(c.id))
                    .filter(c => !addSearch || c.email.toLowerCase().includes(addSearch.toLowerCase()))
                    .slice(0, 60)
                    .map(c => (
                      <button
                        key={c.id}
                        onClick={() => addToSegment.mutate({ segmentId: selected, contactId: c.id })}
                        className="text-xs px-2 py-1 rounded-full bg-white border border-gray-200 hover:border-brand-400 hover:text-brand-600 transition-colors"
                      >
                        {c.email}
                      </button>
                    ))}
                </div>
              </div>
              {/* Current members — full table like Subscribed Members */}
              {!contacts?.length ? (
                <p className="px-4 py-6 text-center text-sm text-gray-400">No members yet — add from the list above, or link leads from the Lead Engine.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-400 uppercase tracking-wide">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-semibold">Email</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Name</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Company</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Status</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {contacts
                        .filter(c => !memberSearch ||
                          c.email.toLowerCase().includes(memberSearch.toLowerCase()) ||
                          `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase().includes(memberSearch.toLowerCase()) ||
                          (c.company ?? '').toLowerCase().includes(memberSearch.toLowerCase()))
                        .map(c => (
                          <tr key={c.id} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 font-medium text-gray-900 text-xs">{c.email}</td>
                            <td className="px-4 py-2.5 text-gray-600 text-xs">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-500 text-xs">{c.company || '—'}</td>
                            <td className="px-4 py-2.5"><StatusBadge status={c.status as ContactStatus} /></td>
                            <td className="px-4 py-2.5 text-right">
                              <button
                                onClick={() => removeFromSegment.mutate({ segmentId: selected, contactId: c.id })}
                                className="text-gray-400 hover:text-red-600 p-1" title="Remove from segment"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-sm text-gray-400">
              Select a segment to manage its contacts
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
